// Token Savings Estimator — typed REST contracts (Tasks 7/8).
//
// Mirrors backend/api/services/token-savings-report.ts exactly (same field
// names) so the frontend never re-derives formulas or labels.

import { apiClient } from './client'

export interface TokenSavingsFormulas {
  returnedTokens: string
  representedSourceTokens: string
  proxyTokens: string
  compressionRatio: string
  savingsPct: string
}

export interface TokenSavingsReportBucket {
  bucketStart: string
  returnedTokens: number
  representedSourceTokens: number
  proxyTokens: number
}

export interface TokenSavingsBreakdownRow {
  key: string
  eligibleCalls: number
  measuredCalls: number
  returnedTokens: number
  representedSourceTokens: number
  proxyTokens: number
}

export interface TokenSavingsReport {
  hours: number
  since: string
  generatedAt: string
  formulas: TokenSavingsFormulas
  disclaimer: string
  tokenizer: { method: 'js-tiktoken'; encodings: string[] }
  coverage: {
    eligibleCalls: number
    measuredCalls: number
    coveragePct: number | null
  }
  proxy: {
    returnedTokens: number
    representedSourceTokens: number
    proxyTokens: number
    compressionRatio: number | null
    savingsPct: number | null
    bySourceQuality: Record<string, number>
  }
  hourlySeries: TokenSavingsReportBucket[]
  byProject: TokenSavingsBreakdownRow[]
  byClient: TokenSavingsBreakdownRow[]
  byTool: TokenSavingsBreakdownRow[]
}

export interface TokenSavingsQuery {
  hours?: number
  client?: string
  tool?: string
}

function toQueryString(q?: TokenSavingsQuery & { projectId?: number; userId?: number }): string {
  if (!q) return ''
  const params = new URLSearchParams()
  if (q.hours) params.set('hours', String(q.hours))
  if (q.client) params.set('client', q.client)
  if (q.tool) params.set('tool', q.tool)
  if (q.projectId !== undefined) params.set('projectId', String(q.projectId))
  if (q.userId !== undefined) params.set('userId', String(q.userId))
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

/** Aggregate across every project the caller may manage (org admins: org-wide). */
export function getDashboardTokenSavings(query?: TokenSavingsQuery): Promise<TokenSavingsReport> {
  return apiClient.get<TokenSavingsReport>(`/dashboard/token-savings${toQueryString(query)}`)
}

/** One project's totals — requires manager (or admin) access. */
export function getProjectTokenSavings(
  projectId: number,
  query?: TokenSavingsQuery
): Promise<TokenSavingsReport> {
  return apiClient.get<TokenSavingsReport>(
    `/projects/${projectId}/token-savings${toQueryString(query)}`
  )
}

/** Admin-only detailed view; additionally accepts projectId/userId filters. */
export function getSettingsTokenSavings(
  query?: TokenSavingsQuery & { projectId?: number; userId?: number }
): Promise<TokenSavingsReport> {
  return apiClient.get<TokenSavingsReport>(`/settings/token-savings${toQueryString(query)}`)
}

export interface TokenSavingsFacets {
  projects: Array<{ id: number; name: string }>
  clients: string[]
  tools: string[]
}

/** Admin-only filter options with data in scope (projects + distinct clients/tools). */
export function getTokenSavingsFacets(): Promise<TokenSavingsFacets> {
  return apiClient.get<TokenSavingsFacets>('/settings/token-savings/facets')
}

/** Dashboard Time Period presets mapped to report hours. */
export const TOKEN_SAVINGS_PERIODS = [
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 168 },
  { label: 'Last 30 days', hours: 720 },
  { label: 'Last 12 months', hours: 8760 },
] as const
